import { ReactNode } from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  OverlayTrigger,
  Tooltip
} from "react-bootstrap";
import type { BreadcrumbItem as BreadcrumbItemType } from "../@types/breadcrumb";

import CopyToClipboard from "../../../components/CopyToClipboard";
import { isBlankOrHangulFiller } from "../../../utils";

export interface RelationshipPath {
  hash_value?: string;
  path: string;
  r?: number;
}

export type BreadcrumbElement = BreadcrumbItemType & {
  item: string;
  title: ReactNode;
  childRoot: boolean;
};

export interface NavBreadcrumbProps {
  sourceFileName: string;
  relationshipPaths?: RelationshipPath[];
  currentPath?: string;
  sampleRelationshipID: number | string;
  sourceHash?: string;
  className?: string;
  innerBreadcrumbClassName?: string;
  onItemClick: (params: { value: BreadcrumbItemType }) => void;
  "data-testid"?: string;
}

const mapRootFile = (sourceFileName: string): BreadcrumbElement => ({
  item: sourceFileName,
  path: "/",
  title: `Browse /`,
  r: 0,
  childRoot: false
});

const getBreadcrumbItemTestId = (
  testId: string | undefined,
  index: number,
  lastPath: boolean
): string | null => {
  if (!testId) return null;
  if (index === 0) {
    return `${testId}_root_item`;
  } else if (lastPath) {
    return `${testId}_last_item`;
  }
  return `${testId}_item_${index}`;
};

const getFileNameDisplay = (data: BreadcrumbElement): string | undefined => {
  if (isBlankOrHangulFiller(data.item)) {
    return data.hashValue;
  }
  return data.item;
};

const mapFetchedRelationshipPaths = (
  fetchedRelationshipPaths: RelationshipPath[],
  sampleRelationshipId: number | string,
  sourceHash?: string
): BreadcrumbElement[] => {
  let fetchedRelationshipBreadcrumbItems: BreadcrumbElement[] = [];
  fetchedRelationshipPaths.forEach((relationshipPath, parentIndex) => {
    const { path, r, hash_value: pathHashValue } = relationshipPath || {};
    const relationshipPathArray = (path || "").split("/") || [];

    fetchedRelationshipBreadcrumbItems = [
      ...fetchedRelationshipBreadcrumbItems,
      ...relationshipPathArray.map((p, index) => {
        let relationshipId: number | null = null;
        let hashValue: string = pathHashValue ?? "";
        let childRoot = false;
        let title: ReactNode = `Browse ${relationshipPathArray
          .slice(0, index + 1)
          .join("/")}`;

        if (
          relationshipPathArray.length - 1 === index &&
          fetchedRelationshipPaths.length - 1 !== parentIndex
        ) {
          const element = fetchedRelationshipPaths[parentIndex + 1];
          relationshipId = element.r ?? 0;
          hashValue = element.hash_value ?? "";
          childRoot = true;
        } else if (
          relationshipPathArray.length - 1 === index &&
          fetchedRelationshipPaths.length - 1 === parentIndex &&
          sourceHash
        ) {
          title = (
            <span>
              This sample is an Extracted File received during Static Analysis
              of:{" "}
              <span className="last_item_breadcrumb_tooltip">{`${sourceHash.slice(
                0,
                12
              )}...`}</span>
            </span>
          );
        } else {
          relationshipId = r ?? 0;
        }

        return {
          item: p,
          path:
            relationshipPathArray.length - 1 === index
              ? "/"
              : relationshipPathArray.slice(0, index + 1).join("/"),
          title,
          r: relationshipId,
          hashValue,
          childRoot
        } as BreadcrumbElement;
      })
    ];
  });
  return fetchedRelationshipBreadcrumbItems;
};

const mapCurrentFilePath = (
  currentPath: string | undefined,
  sampleRelationshipId: number | string
): BreadcrumbElement[] => {
  const currentPathArray =
    currentPath && currentPath !== "/" ? currentPath.split("/") : [];
  return currentPathArray.map((p, index) => ({
    item: p,
    path: currentPathArray.slice(0, index + 1).join("/"),
    title: `Browse ${currentPathArray.slice(0, index + 1).join("/")}`,
    r: Number(sampleRelationshipId),
    childRoot: false,
    hashValue: ""
  }));
};

const mapToBreadCrumbItems = (
  sourceFileName: string,
  fetchedRelationshipPaths: RelationshipPath[] = [],
  sampleRelationshipId: number | string,
  currentPath: string | undefined = "",
  sourceHash?: string
): BreadcrumbElement[] => {
  const rootBreadcrumbItem = mapRootFile(sourceFileName);
  const fetchedRelationshipBreadcrumbItems = mapFetchedRelationshipPaths(
    fetchedRelationshipPaths,
    sampleRelationshipId,
    sourceHash
  );
  const currentPathBreadcrumbItems = mapCurrentFilePath(
    currentPath,
    sampleRelationshipId
  );
  return [
    rootBreadcrumbItem,
    ...fetchedRelationshipBreadcrumbItems,
    ...currentPathBreadcrumbItems
  ];
};

const NavBreadcrumb = ({
  sourceFileName,
  relationshipPaths = [],
  currentPath,
  sampleRelationshipID,
  sourceHash,
  className,
  innerBreadcrumbClassName,
  onItemClick,
  "data-testid": dataTestId
}: NavBreadcrumbProps) => {
  const breadcrumbItems = mapToBreadCrumbItems(
    sourceFileName,
    relationshipPaths,
    sampleRelationshipID,
    currentPath,
    sourceHash
  );

  const filteredBreadcrumbItems = breadcrumbItems.filter(p => p.path);

  return (
    <Breadcrumb
      className={`extracted-files-breadcrumb ${className || ""}`}
      listProps={{ className: innerBreadcrumbClassName }}
      data-testid={dataTestId}
    >
      {filteredBreadcrumbItems.map((item, index) => {
        const lastPath = index === breadcrumbItems.length - 1;
        const itemTestId = getBreadcrumbItemTestId(dataTestId, index, lastPath);
        return (
          <OverlayTrigger
            key={`breadcrumb-overlay-${index}`}
            placement="bottom"
            overlay={
              <Tooltip
                className="breadcrumb-tooltip"
                id={`${Date.now()}`}
                data-testid={itemTestId ? `${itemTestId}_tooltip` : undefined}
              >
                {item.title}
              </Tooltip>
            }
          >
            <BreadcrumbItem
              key={`breadcrumb-item-${item.path}`}
              active={lastPath}
              onClick={() => {
                if (!lastPath) {
                  onItemClick({ value: item });
                }
              }}
              data-testid={itemTestId ?? undefined}
            >
              {index === 0 || lastPath || item.childRoot
                ? getFileNameDisplay(item)
                : "..."}
            </BreadcrumbItem>
          </OverlayTrigger>
        );
      })}
      <CopyToClipboard
        value={getFileNameDisplay(
          filteredBreadcrumbItems.at(-1) as BreadcrumbElement
        )}
        containerClassName="ml-1"
        setContainerLineHeight={false}
      />
    </Breadcrumb>
  );
};

export default NavBreadcrumb;
